<template>
  <vx-card class="mt-5">
    <div v-if="loading" class="flex justify-center items-center py-10">
      <vs-spinner color="primary" />
    </div>

    <div v-else-if="doc" class="p-6 invoice-detail">
      <div class="flex flex-wrap items-center justify-between mb-base">
        <h3 class="mb-0">#{{ doc.docNo || '—' }}</h3>
        <div class="flex flex-wrap items-center">
          <vs-button type="border" class="mr-3" @click="onBack">{{ $t('dashboard.back') }}</vs-button>
          <vs-button v-if="doc.channelUrl" type="border" @click="onOpenChannel">
            View in e-Invoice Exchange
          </vs-button>
        </div>
      </div>

      <!-- Supplier Info -->
      <vx-card class="mt-base" :title="$t('labels.supplier')" no-shadow card-border>
        <div class="pl-3 pr-3 vx-row">
          <div class="w-full mt-3 md:w-1/2 sm:w-1/2">
            <label class="text-sm text-black">{{ $t('dashboard.id') }}:</label><br />
            <p>{{ doc.supplierTaxId || '—' }}</p>
          </div>
          <div class="w-full mt-3 md:w-1/2 sm:w-1/2">
            <label class="text-sm text-black">{{ $t('dashboard.name') }}:</label><br />
            <p>{{ doc.supplierName || '—' }}</p>
          </div>
        </div>
      </vx-card>

      <!-- Overview Info -->
      <vx-card class="mt-base" :title="$t('labels.overview')" no-shadow card-border>
        <div class="pl-3 pr-3 vx-row">
          <div class="w-full md:w-1/2 sm:w-1/2">
            <label class="text-sm text-black">{{ $t('dashboard.docNo') }}:</label><br />
            <p>{{ doc.docNo || '—' }}</p>
          </div>
          <div class="w-full md:w-1/2 sm:w-1/2">
            <label class="text-sm text-black">{{ $t('dashboard.status') }}:</label><br />
            <vs-chip :color="doc.status.color" class="product-order-status">{{ doc.status.label }}</vs-chip>
          </div>
          <div class="w-full mt-3 md:w-1/2 sm:w-1/2">
            <label class="text-sm text-black">{{ $t('dashboard.documentType') }}:</label><br />
            <p>{{ doc.typeLabel || '—' }}</p>
          </div>
          <div class="w-full mt-3 md:w-1/2 sm:w-1/2">
            <label class="text-sm text-black">{{ $t('dashboard.date') }}:</label><br />
            <p>{{ doc.issueDate ? $moment(doc.issueDate).format('DD MMM YYYY') : '—' }}</p>
          </div>
          <div v-if="doc.dueDate" class="w-full mt-3 md:w-1/2 sm:w-1/2">
            <label class="text-sm text-black">{{ $t('dashboard.dueAt') }}:</label><br />
            <p>{{ $moment(doc.dueDate).format('DD MMM YYYY') }}</p>
          </div>
          <div v-if="doc.discount != null" class="w-full mt-3 md:w-1/2 sm:w-1/2">
            <label class="text-sm text-black">{{ $t('dashboard.totalDiscount') }}:</label><br />
            <p class="text-bold">{{ doc.discount | toCurrency(doc.currency || '') }}</p>
          </div>
          <div v-if="doc.subTotal != null" class="w-full mt-3 md:w-1/2 sm:w-1/2">
            <label class="text-sm text-black">{{ $t('dashboard.subtotal') }}:</label><br />
            <p class="text-bold">{{ doc.subTotal | toCurrency(doc.currency || '') }}</p>
          </div>
          <div v-if="doc.tax != null" class="w-full mt-3 md:w-1/2 sm:w-1/2">
            <label class="text-sm text-black">{{ $t('dashboard.totalTax') }}:</label><br />
            <p class="text-bold">{{ doc.tax | toCurrency(doc.currency || '') }}</p>
          </div>
          <div class="w-full mt-3 md:w-1/2 sm:w-1/2">
            <label class="text-sm text-black">{{ $t('dashboard.total') }}:</label><br />
            <p class="text-bold">{{ (doc.total || 0) | toCurrency(doc.currency || '') }}</p>
          </div>
          <div v-if="doc.remarks" class="w-full mt-3 md:w-1/2 sm:w-1/2">
            <label class="text-sm text-black">{{ $t('dashboard.remarks') }}:</label><br />
            <p class="text-bold">{{ doc.remarks }}</p>
          </div>
        </div>
      </vx-card>

      <!-- Items Info -->
      <vx-card class="mt-base" no-shadow card-border>
        <h4 class="mb-3 card-title">{{ $t('labels.items') }} ({{ doc.items.length }})</h4>
        <vs-table
          pagination
          max-items="20"
          stripe
          :data="doc.items"
          :noDataText="$t('dashboard.noDataAvailable')"
        >
          <template slot="thead">
            <vs-th>{{ $t('dashboard.item') }}</vs-th>
            <vs-th>{{ $t('dashboard.description') }}</vs-th>
            <vs-th>{{ $t('dashboard.quantity') }}</vs-th>
            <vs-th>{{ $t('dashboard.unitCost') }}</vs-th>
            <vs-th>{{ $t('dashboard.tax') }}</vs-th>
            <vs-th>{{ $t('dashboard.subtotal') }}</vs-th>
            <vs-th>{{ $t('dashboard.total') }}</vs-th>
          </template>

          <template slot-scope="{ data }">
            <tbody>
              <vs-tr :data="tr" :key="indextr" v-for="(tr, indextr) in data">
                <vs-td><p>{{ tr.item || '—' }}</p></vs-td>
                <vs-td><p>{{ tr.description }}</p></vs-td>
                <vs-td><p>{{ tr.quantity != null ? tr.quantity : '—' }}</p></vs-td>
                <vs-td><p>{{ (tr.unitPrice || 0) | formatNumber }}</p></vs-td>
                <vs-td><p>{{ (tr.tax || 0) | formatNumber }}</p></vs-td>
                <vs-td><p>{{ (tr.subTotal || 0) | formatNumber }}</p></vs-td>
                <vs-td><p>{{ (tr.total || 0) | toCurrency(doc.currency || '') }}</p></vs-td>
              </vs-tr>
            </tbody>
          </template>
        </vs-table>
      </vx-card>
    </div>
  </vx-card>
</template>

<script>
import { SOURCES, jurisdictionOf } from './expenseDocSources'

// Purchases > Expense Doc detail — laid out like the Sales invoice detail
// (Supplier / Overview / Items), channel-neutral. LHDN / Peppol specifics and
// actions (reject, respond) are one click away in e-Invoice Exchange.
export default {
  data() {
    return {
      loading: false,
      doc: null,
    }
  },
  computed: {
    source() {
      return SOURCES[jurisdictionOf(this.$store)]
    },
  },
  watch: {
    '$route.params.id'() {
      this.fetch()
    },
  },
  created() {
    this.fetch()
  },
  methods: {
    async fetch() {
      this.loading = true
      try {
        const raw = await this.$store.dispatch(this.source.detailAction, this.$route.params.id)
        this.doc = raw ? this.source.toDetail(raw) : null
      } catch (e) {
        console.error('[ExpenseDocs] detail fetch failed:', e)
        this.$vs.notify({
          color: 'danger',
          title: 'Error',
          text: e.response?.data?.message || 'Failed to fetch expense document',
        })
      } finally {
        this.loading = false
      }
    },
    onBack() {
      const type = this.doc ? this.doc.typeRoute : 'invoice'
      this.$router.push(`/expense-docs/type/${type}`).catch(() => {})
    },
    onOpenChannel() {
      this.$router.push(this.doc.channelUrl).catch(() => {})
    },
  },
}
</script>
